import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';

import { API_URL } from 'constant';
import { REACT_APP_API_HOST } from 'envs';
import {
	IAction,
	IMetadata,
	IPipelineSteps,
	PipelineMasterSteps,
	PipelineSettingsFormState,
	PipelineTemplateState,
	SubscriptionPriceState,
} from 'global-stores';
import { GoogleFontsState } from 'global-stores/google-fonts';
import { useNetwork } from 'hooks';
import { WebWorker } from 'hooks/worker';
import { SettingFormInitState } from 'views/onboarding-flow';

export const useDashboradhook = () => {
	const setPipelineMasterSteps = useSetRecoilState(PipelineMasterSteps);
	const setPipelineStateForm = useSetRecoilState(PipelineSettingsFormState);
	const setSettingFormInit = useSetRecoilState(SettingFormInitState);
	const setGoogleFonts = useSetRecoilState(GoogleFontsState);
	const setTemplateInit = useSetRecoilState(PipelineTemplateState);
	const setSubscriptionPrice = useSetRecoilState(SubscriptionPriceState);

	const { get: getPipelineInits, accessToken } = useNetwork();
	const { get: getTemplateList } = useNetwork({
		returnResponse: true,
		updateState: false,
	});
	const { get: getSubsPrice } = useNetwork({
		returnResponse: true,
		updateState: false,
	});

	const getPipelineInit = useCallback(() => {
		getPipelineInits(`${API_URL.PIPELINE_INIT}`).then(resp => {
			const { data: allDatas } = resp ?? {};
			const data = allDatas as IPipelineSteps[];
			if (data) {
				try {
					/**
					 * Todo @Paras this is to hide proof read from beta please remove this once we are good to go in beta
					 * */
					const steps: IPipelineSteps[] = data.map((step: any) => {
						const actions = step.actions.map((action: any) => ({
							...action,
							stepKey: step.key,
						}));
						return {
							...step,
							actions,
						};
					});

					// Per-pipeline provider selection: the dropdown injected onto each verification step
					// (KYC/KYB/AML) so it renders even when the backend seed predates the feature. The
					// leading "Business default" resolves to the business's provider at runtime.
					const PROVIDER_SELECTS: { [k: string]: IMetadata } = {
						kyc: {
							type: 'select',
							key: 'kycProvider',
							label: 'KYC Provider',
							description: '',
							default: 'businessDefault',
							options: [
								{ label: 'Business default', value: 'businessDefault' },
								{ label: 'DIDIT (document-less)', value: 'DIDIT' },
							],
						},
						// __simplici_kyb_thekyb_only_provider__: no `kyb` entry -- KYB runs on
						// theKYB and that is not a per-flow choice, so the step offers no
						// provider dropdown. LexisNexis is being retired and Enigma is not
						// wired up, so the other two options only let someone build a flow
						// that cannot run. Removed here, at the injection source, rather than
						// hidden at the render layer, so the configurator, the canvas tile and
						// the price summary all agree there is nothing to configure. The
						// provider actually used is resolved from the business, not from here.
						// Restoring the entry below brings the dropdown back everywhere.
						// __simplici_aml_watcher_default__: no `aml` entry -- the AML provider
						// is a business-level setting, not a per-flow choice, so the step
						// offers no provider dropdown and therefore no settings at all. The
						// seed in aml-action.tsx stays `businessDefault`, which resolves to
						// whatever the business has configured. Removed here,
						// at the injection source, rather than hidden at the render layer, so
						// the configurator, the canvas tile and the price summary all agree
						// that there is nothing to configure. The provider actually sent is
						// seeded by aml-action (AML_WATCHER). Restoring the entry below brings
						// the dropdown back everywhere.
					};

					//@avinashSatschel: commented Docusign as no longer use
					const updatedSteps = steps?.map(step => {
						if (step.actions?.length) {
							step.actions.forEach(action => {
								if (action.metadata && action.metadata.length > 0) {
									//@avinashSatschel
									// This code combines the prices for "ssn" and "addressVerification" into one item and then removes "ssn" from the metadata array, as required.
									// Check if the current step's key is 'kyc' requirement came from @abhishekMahra
									if (step.key === 'kyc') {
										// Find the index of the 'addressVerification' item in the action.metadata array
										const addressIndex = action.metadata.findIndex(
											el => el.key === 'addressVerification'
										);
										// Check if 'addressVerification' was found in the metadata (index is not -1) and exists at that index
										if (addressIndex !== -1 && action.metadata[addressIndex]) {
											// Find the 'ssn' item in metadata and get its price, or default to 0 if 'ssn' doesn't exist
											const ssnPrice =
												action.metadata.find(el => el.key === 'ssn')?.price ??
												0;
											// Create a payload object with a combined price of 'addressVerification' and 'ssn'
											const payload = {
												price:
													(action.metadata[addressIndex]?.price ?? 0) +
													ssnPrice,
											};
											// Update the 'addressVerification' item with the new combined price
											Object.assign(action.metadata[addressIndex], payload);
										}
										// Remove the 'ssn' item from the metadata array as it has been merged with 'addressVerification'
										action.metadata = action.metadata.filter(
											el => el.key !== 'ssn'
										);
									}
									action.metadata.forEach(meta => {
										if (meta.options) {
											meta.options = meta.options.filter(
												opt => opt.key !== 'docusign'
											);
										}
									});
								}
							});
						}
						// Inject the per-pipeline provider dropdown (idempotent — skip if already seeded).
						const providerSelect = PROVIDER_SELECTS[step.key];
						if (providerSelect && step.actions?.[0]) {
							if (!step.actions[0].metadata) {
								step.actions[0].metadata = [];
							}
							const md = step.actions[0].metadata;
							if (!md.some(m => m.key === providerSelect.key)) {
								md.push({ ...providerSelect });
							}
						}
						return step;
					});
					setPipelineMasterSteps(updatedSteps);
					const newState: { [key: string]: boolean | string | undefined } = {};
					data.forEach((step: IPipelineSteps) => {
						step.actions.forEach((action: IAction) => {
							action.metadata.forEach((meta: IMetadata) => {
								const { key } = meta;
								if (meta.default === 'true') {
									return (newState[key] = true);
								}
								if (meta.default === 'false') {
									return (newState[key] = false);
								}
								if (
									key === 'accreditationType' ||
									key === 'accreditationDocument'
								) {
									return (newState[key] = '');
								}
								if (
									key === 'reminderSettingEmailMessage' ||
									key === 'reminderSettingTextMessage'
								) {
									const value = meta?.default?.replace(
										' to recieve your {shares}',
										''
									);
									return (newState[key] = value ?? '');
								}
								return (newState[key] = meta.default);
							});
						});
					});
					setSettingFormInit(newState);
					setPipelineStateForm(newState);
				} catch (error) {
					/* empty */
				}
			}
		});
	}, [
		getPipelineInits,
		setPipelineMasterSteps,
		setPipelineStateForm,
		setSettingFormInit,
	]);

	const fetchGoogleFonts = useCallback(() => {
		const url = `${REACT_APP_API_HOST}${API_URL.GOOGLE_FONDS}`;
		WebWorker.worker.postMessage({
			endpoint: url,
			config: {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${accessToken}`,
				},
			},
			nodeId: '',
		});
		WebWorker.worker.onmessage = resp => {
			const { status, data } = resp.data;
			if (status === 200) {
				setGoogleFonts(data);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken]);

	const getTemplateInit = async () => {
		const resp = await getTemplateList(API_URL.STEP_TEMPLATES);
		if (resp?.apiPayload?.message.toLowerCase() === 'ok') {
			const { data } = resp.apiPayload;
			setTemplateInit(data ?? {});
		}
	};

	const getSubscriptionPrice = async () => {
		const resp = await getSubsPrice(API_URL.SUBSCRIPTION_PRICE);
		if (resp?.apiPayload?.message.toLowerCase() === 'ok') {
			const { data } = resp.apiPayload;
			setSubscriptionPrice(data);
		}
	};
	return {
		getPipelineInit,
		fetchGoogleFonts,
		getTemplateInit,
		getSubscriptionPrice,
	};
};
